import React from 'react'
import ButtonBase from '@mui/material/ButtonBase'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { alpha } from '@mui/material/styles'
import CheckCircle from '@mui/icons-material/CheckCircle'
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { formatHours, formatPercent } from './loadCapacityFormat'

// Story 175, DN4/RN58 -- the cards are painted in the order `groups` arrives: Sun already applies H153·P5
// (named groups by name, `ungrouped` last) with its own collation, and a second sort here could disagree
// with it (accents, digits). `groups` always arrives complete (contract §2): nothing is filtered either.

// Story 175, DN7/RN59 -- the saturated-periods literal of each grouping of the response.
const SATURATED_CAPTION_KEY = {
  WEEK: '{{count}} saturated weeks · max {{ratio}} %',
  MONTH: '{{count}} saturated months · max {{ratio}} %'
}

const groupCardKey = (group) => (group.ungrouped ? 'ungrouped' : group.deviceGroupId)

// Story 177 (AP12), RN58 -- a group reads as a button that can be pressed: border, a slight shadow, a change on
// hover and a circle that is ticked on the chosen one.
const cardSx = (theme, selected, saturated) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1,
  textAlign: 'left',
  minWidth: '10.5rem',
  padding: '0.6rem 0.9rem',
  borderRadius: 2,
  border: `1px solid ${selected ? theme.palette.success.main : theme.palette.divider}`,
  backgroundColor: selected ? alpha(theme.palette.success.main, 0.14) : theme.palette.action.hover,
  boxShadow: selected ? `0 0 0 1px ${alpha(theme.palette.success.main, 0.5)}` : '0 1px 2px rgba(0, 0, 0, 0.35)',
  transition: 'background-color 0.15s, border-color 0.15s',
  '&:hover': {
    backgroundColor: selected ? alpha(theme.palette.success.main, 0.2) : theme.palette.action.selected,
    borderColor: saturated ? theme.palette.error.main : theme.palette.success.light
  }
})

const SelectionMark = ({ selected }) => (selected
  ? <CheckCircle data-testid='load-capacity-group-check' sx={{ fontSize: 18, color: 'success.main', marginTop: '1px' }} />
  : <RadioButtonUnchecked sx={{ fontSize: 18, color: 'text.disabled', marginTop: '1px' }} />)

/**
 * CU8 -- the group cards double as the group selector: "All groups" first, one card per entry of
 * `groups` (always the complete list, RN4), "No group" last when an `ungrouped` entry exists. Each
 * card summarises its machine count, weekly OEE capacity (RN29) and maximum utilisation, in red with
 * its saturated-period count when `saturated === true`. Picking a card never removes any other card
 * (S90): only the caller's `deviceGroupId`/`ungrouped` filter changes. `groups` keeps painting even
 * when `buckets` is empty (S94).
 */
const LoadCapacityGroupCards = ({
  groups = [],
  deviceGroupId = null,
  ungrouped = false,
  onChange = () => {},
  allGroupsSummary = null,
  grouping = 'WEEK'
}) => {
  const { t_, i18n } = useSmartTranslation()
  const locale = i18n ? i18n.language : undefined
  const isAllSelected = deviceGroupId === null && !ungrouped
  const saturatedGroupCount = groups.filter((group) => group.saturated).length

  const renderGroupCard = (group) => {
    const key = groupCardKey(group)
    const selected = group.ungrouped ? ungrouped : (!ungrouped && deviceGroupId === group.deviceGroupId)
    // INV-5/S89 -- a group without any capacity cell has no maximum, never "0 %"; DN7 -- and it says so in
    // one literal ("max not available"), not "max Not available %".
    const hasMaximum = group.maxUtilizationRatioPercent !== null && group.maxUtilizationRatioPercent !== undefined
    const ratioForCaption = hasMaximum ? formatPercent(group.maxUtilizationRatioPercent, locale) : null
    return (
      <ButtonBase
        key={key}
        focusRipple
        onClick={() => onChange(group.ungrouped
          ? { deviceGroupId: null, ungrouped: true }
          : { deviceGroupId: group.deviceGroupId, ungrouped: false })}
        aria-pressed={selected}
        data-testid={group.ungrouped ? 'load-capacity-chip-no-group' : `load-capacity-chip-group-${group.deviceGroupId}`}
        sx={(theme) => cardSx(theme, selected, group.saturated)}
      >
        <SelectionMark selected={selected} />
        <Stack
          spacing={0.25}
          data-testid={`load-capacity-group-card-${key}`}
          data-saturated={group.saturated}
          data-selected={selected}
        >
          <Typography variant='subtitle2' component='div' noWrap>
            {group.ungrouped ? t_('No group') : group.deviceGroupName}
          </Typography>
          <Typography variant='caption' color='text.secondary' component='div' data-testid={`load-capacity-group-card-${key}-summary`}>
            {t_('{{count}} machines · {{hours}} h/week', {
              count: group.deviceCount,
              hours: formatHours(group.weeklyCapacityHours, locale)
            })}
          </Typography>
          {group.saturated
            ? (
              <Typography variant='caption' color='error.main' component='div' sx={{ fontWeight: 600 }} data-testid={`load-capacity-group-card-${key}-saturated`}>
                {hasMaximum
                  ? t_(SATURATED_CAPTION_KEY[grouping] ?? SATURATED_CAPTION_KEY.WEEK, { count: group.saturatedPeriodCount, ratio: ratioForCaption })
                  : t_('max not available')}
              </Typography>
              )
            : (
              <Typography variant='caption' color='success.main' component='div' sx={{ fontWeight: 600 }} data-testid={`load-capacity-group-card-${key}-max`}>
                {hasMaximum ? t_('max {{ratio}} %', { ratio: ratioForCaption }) : t_('max not available')}
              </Typography>
              )}
        </Stack>
      </ButtonBase>
    )
  }

  return (
    <Paper variant='outlined' sx={{ borderRadius: 3, padding: '0.75rem 1rem' }} data-testid='load-capacity-groups-section'>
      <Stack direction='row' alignItems='baseline' spacing={1.5} sx={{ marginBottom: 1.25 }}>
        <Typography sx={{ fontWeight: 700 }} data-testid='load-capacity-groups-title'>{t_('Machine groups')}</Typography>
        <Typography variant='caption' color='text.secondary' data-testid='load-capacity-groups-hint'>{t_('Pick a group to see its load')}</Typography>
      </Stack>
      <Stack direction='row' spacing={1.25} flexWrap='wrap' useFlexGap data-testid='load-capacity-group-cards'>
        <ButtonBase
          focusRipple
          onClick={() => onChange({ deviceGroupId: null, ungrouped: false })}
          aria-pressed={isAllSelected}
          data-testid='load-capacity-chip-all-groups'
          sx={(theme) => cardSx(theme, isAllSelected, saturatedGroupCount > 0)}
        >
          <SelectionMark selected={isAllSelected} />
          <Stack spacing={0.25} data-testid='load-capacity-group-card-all' data-selected={isAllSelected}>
            <Typography variant='subtitle2' component='div'>{t_('All groups')}</Typography>
            {allGroupsSummary
              ? (
                <Typography variant='caption' color='text.secondary' component='div' data-testid='load-capacity-group-card-all-summary'>
                  {typeof allGroupsSummary.weeklyCapacityHours !== 'number'
                    ? t_('{{count}} machines', { count: allGroupsSummary.deviceCount })
                    : t_('{{count}} machines · {{hours}} h/week', {
                      count: allGroupsSummary.deviceCount,
                      hours: formatHours(allGroupsSummary.weeklyCapacityHours, locale)
                    })}
                </Typography>
                )
              : null}
            <Typography
              variant='caption'
              component='div'
              sx={{ fontWeight: 600 }}
              color={saturatedGroupCount > 0 ? 'error.main' : 'success.main'}
              data-testid='load-capacity-group-card-all-saturation'
            >
              {saturatedGroupCount === 0
                ? t_('No saturated group')
                : (saturatedGroupCount === 1
                    ? t_('One group with saturation')
                    : t_('{{count}} groups with saturation', { count: saturatedGroupCount }))}
            </Typography>
          </Stack>
        </ButtonBase>
        {groups.map(renderGroupCard)}
      </Stack>
    </Paper>
  )
}

export default LoadCapacityGroupCards
